import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  FieldVariant,
  TextFieldProps,
  SwitchFieldProps,
  SelectOption,
  SelectFieldProps,
  EditorFieldProps,
} from '@/components/form-builder/form-builder.types';
import { PreviewPdfComponent } from '@/components/shared/preview-pdf/preview-pdf.component';
import { DacRepository } from '@/store/dac-state/dac-state.repository';
import { BehaviorSubject, combineLatest, map, Observable, of } from 'rxjs';
import { DacPrixFormComponent } from '../../dac-prix-form/dac-prix-form.component';
import { DacShortlistFormComponent } from '../../dac-shortlist-form/dac-shortlist-form.component';
import { Company, DocType, User } from '@/types';
import { MultiFormAccordionComponent } from '@/components/multi-form-accordion/multi-form-accordion.component';
import { DacSubmissionPiecesComponent } from '../../dac-submission-pieces/dac-submission-pieces.component';
import { MultiFormAccordionService } from '@/components/multi-form-accordion/multi-form-accordion.service';
import { ObservableFormBuilderResolverComponent } from '@/components/form-builder/utils/observable-form-builder-resolver';
import { DacResponsableInfosComponent } from '../../dac-responsable-infos/dac-responsable-infos.component';

interface getDacInspectFormObject {
  store: DacRepository;
  translate: (key: string, obj: any) => string;
  dacId: number | null;
  dacVer: number | null;
  pdfUrl?: BehaviorSubject<string>;
  currencyOptions?: SelectOption[];
  nonBlacklistedCompanies?: Observable<SelectOption[]>;
  memberOptions: Observable<User[]>;
  multiFormAccordionsForSubmissionPieces: Observable<MultiFormAccordionService[]>;
  docTypes: Observable<DocType[]>;
}

export const getDacInspectFormObject = ({
  store,
  translate,
  dacId,
  dacVer,
  pdfUrl,
  currencyOptions = [],
  nonBlacklistedCompanies,
  memberOptions,
  multiFormAccordionsForSubmissionPieces,
  docTypes,
}: getDacInspectFormObject): DynamicForm => {
  const aoField: DynamicField<TextFieldProps> = {
    label: 'dac.form.ao',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'aoReference'),
    },
  };

  const refField: DynamicField<TextFieldProps> = {
    label: 'dac.form.reference',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'reference'),
    },
  };

  const ppmField: DynamicField<TextFieldProps> = {
    label: 'dac.form.ppm',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'ppm', 'nom'),
    },
  };

  const milestoneField: DynamicField<TextFieldProps> = {
    label: 'dac.form.milestone',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'realisation', 'realisationEnvisagees'),
    },
  };

  const marketTypeField: DynamicField<TextFieldProps> = {
    label: 'dac.form.marketType',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'typeMarche', 'description'),
    },
  };

  const passingModeField: DynamicField<TextFieldProps> = {
    label: 'dac.form.passingMode',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'modePassation', 'libelle'),
    },
  };

  const responsableField: DynamicField<SelectFieldProps> = {
    label: 'dac.form.responsable',
    variant: FieldVariant.SELECT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      options: memberOptions.pipe(map(users => users.map(user => ({ name: `${user.firstName} ${user.lastName}`, code: user.id })))),
      value: store.getNestedObservable<SelectOption>('responsableSelected'),
    },
  };

  const responsableInformationCustomField: DynamicField<CustomFieldProps> = {
    label: '',
    variant: FieldVariant.CUSTOM,
    isHidden: store.getObservable<SelectOption>('responsableSelected').pipe(map(responsable => !responsable.code)),
    props: {
      component: DacResponsableInfosComponent,
      injectKeys: ['responsableData'],
      responsableData: combineLatest([store.getNestedObservable<SelectOption>('responsableSelected'), memberOptions]).pipe(
        map(([selectoption, members]) => {
          if (!selectoption) return undefined;
          if (!members) return undefined;
          const member = members.find(member => member.id === selectoption?.code);
          return {
            firstName: member?.firstName,
            lastName: member?.lastName,
            email: member?.email,
            tel: member?.tel,
            adresse: member?.adresse,
          };
        })
      ),
    },
  };

  const conditionField: DynamicField<EditorFieldProps> = {
    label: 'dac.form.condition',
    variant: FieldVariant.EDITOR,
    isRequired: false,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('dacInfo', 'conditionsParticipation'),
      height: '320px',
    },
  };

  const payantField: DynamicField<SwitchFieldProps> = {
    label: 'dac.form.payed',
    variant: FieldVariant.SWITCH,
    isRequired: false,
    isDisabled: of(true),
    props: {
      checked: store.getNestedObservable<boolean>('dacInfo', 'payant'),
    },
  };

  const dacPrixField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.prices',
    variant: FieldVariant.CUSTOM,
    isDisabled: of(true),
    isHidden: store.getNestedObservable<boolean>('dacInfo', 'payant').pipe(map(payant => !payant)),
    props: {
      component: DacPrixFormComponent,
      injectKeys: ['value', 'onChange', 'currencyOptions', 'showField', 'disabled', 'errors', 'globalError', 'hiddenHeader'],
      value: store.getNestedObservable('dacInfo.dacPrix'),
      currencyOptions: currencyOptions,
      showField: store.getNestedObservable<boolean>('dacInfo', 'payant'),
      disabled: of(true),
      hiddenHeader: of(true),
      errors: of([]),
      globalError: of(''),
    },
  };

  const shortListingMembersField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.shortListing',
    variant: FieldVariant.CUSTOM,
    isRequired: false,
    isHidden: store.getNestedObservable<boolean>('dacInfo', 'shortListing').pipe(map(shortListing => !shortListing)),
    props: {
      component: DacShortlistFormComponent,
      injectKeys: ['value', 'onChange', 'options', 'disabled', 'errors', 'globalError', 'hiddenHeader', 'displayAsList'],
      displayAsList: true,
      options: nonBlacklistedCompanies,
      value: store.getObservable('dacInfo').pipe(
        map((dacInfo: any) => dacInfo?.dacShortlist?.map((company: any) => Array.isArray(company) ? company.map((c: any) => c.id) : company.id) || [])
      ),
      onChange: (value: number[]) => {
        store.setNested('dacUpdateDto', 'shortlistMembers', value);
      },
      disabled: of(true),
    },
  };


  const submissionFolderField: (type: DocType, injectedService: MultiFormAccordionService) => DynamicField<CustomFieldProps> = (
    type,
    injectedService
  ) => ({
    label: translate(`dac.form.submissionPiece`, { type: type?.libelle.toLowerCase() }),
    variant: FieldVariant.CUSTOM,
    props: {
      component: MultiFormAccordionComponent,
      injectKeys: ['accordionObject'],
      accordionObject: {
        titles: [translate('dac.form.submissionPiece2', { type: type?.libelle.toLowerCase() })],
        component: DacSubmissionPiecesComponent,
        injectKeys: ['dacId', 'dacVer', 'docTypeId', 'isDisabled'],
        dacId,
        dacVer,
        isDisabled: of(true),
        allowEmpty: of(true),
        docTypeId: type?.id,
        customFactoryMultiFormAccordion: injectedService,
      },
    },
  });

  const submissionFolderObservableSubFormBuilder = combineLatest([docTypes, multiFormAccordionsForSubmissionPieces]).pipe(
    map(
      ([docTypesValue, multiFormAccordionForSubmissionPiecesValue]) =>
        ({
          title: '',
          description: '',
          isHeaderHidden: true,
          grids: [
            {
              title: '',
              isHeaderHidden: true,
              gridItems: docTypesValue.map((docType, index) => ({
                fields: [submissionFolderField(docType, multiFormAccordionForSubmissionPiecesValue[index])],
              })),
            },
          ],
        }) satisfies DynamicForm
    )
  );

  const previewField: DynamicField<CustomFieldProps> = {
    label: 'dac.form.preview',
    variant: FieldVariant.CUSTOM,
    props: {
      component: PreviewPdfComponent,
      injectKeys: ['src'],
      src: pdfUrl,
    },
  };

  return {
    title: 'Document dynamique',
    description: '',
    isHeaderHidden: true,
    grids: [
      {
        title: 'dac.form.tabs.overview',
        gridItems: [
          {
            fields: [aoField, refField],
          },
          {
            fields: [ppmField, milestoneField],
          },
          {
            fields: [marketTypeField, passingModeField],
          },
          {
            fields: [responsableField],
          },
          { fields: [responsableInformationCustomField] },
          {
            fields: [payantField],
          },
          { fields: [shortListingMembersField] },
          {
            fields: [dacPrixField],
          },
          { fields: [conditionField] },
        ],
      },
      // {
      //   title: 'dac.form.tabs.transactional',
      //   gridItems: [
      //     {
      //       fields: [transactionalFormField],
      //     },
      //   ],
      // },
      {
        title: 'dac.form.tabs.submission',
        gridItems: [
          {
            fields: [
              {
                label: '',
                variant: FieldVariant.CUSTOM,
                props: {
                  component: ObservableFormBuilderResolverComponent,
                  injectKeys: ['formObject'],
                  formObject: submissionFolderObservableSubFormBuilder,
                },
              },
            ],
          },
        ],
      },
      {
        title: 'dac.form.tabs.dynamic',
        gridItems: [{ fields: [previewField] }],
      },
    ],
  };
};
